import Api from "../../../core/data/redux/utils/apiCall";
import { API_URLS } from "../../../core/data/redux/apis/urls";

export const ID_CARD_STATUS_MAP: Record<string, { labelKey: string; label: string; cls: string }> = {
  N: { labelKey: "new", label: "New", cls: "bg-warning text-dark" },
  T: { labelKey: "to_be_approved", label: "To Be Approved", cls: "bg-warning text-dark" },
  P: { labelKey: "partially_approved", label: "Partially Approved", cls: "bg-warning text-dark" },
  A: { labelKey: "approved", label: "Approved", cls: "bg-success text-white" },
  R: { labelKey: "rejected", label: "Rejected", cls: "bg-danger text-white" },
  D: { labelKey: "processing_complete", label: "Processing Complete", cls: "bg-success text-white" },
  X: { labelKey: "deleted", label: "Deleted", cls: "bg-secondary text-white" },
  B: { labelKey: "to_be_processed", label: "To Be Processed", cls: "bg-warning text-dark" },
  C: { labelKey: "closed", label: "Closed", cls: "bg-success text-white" },
  K: { labelKey: "cancelled", label: "Cancelled", cls: "bg-secondary text-white" },
  S: { labelKey: "settled", label: "Settled", cls: "bg-success text-white" },
  DRAFT: { labelKey: "draft", label: "Draft", cls: "bg-info text-white" },
  IN_SAP_PROCESS: { labelKey: "in_sap_process", label: "In SAP Process", cls: "bg-info text-white" },
};

export const ID_CARD_STATUS_KEYS = ["", "N", "T", "P", "A", "R", "D", "X", "B", "C", "K", "S", "DRAFT"];

export const BLOOD_GROUPS = ["O+", "O-", "A+", "A-", "B+", "B-", "AB+", "AB-", "BGRP"];

export function getLoggedInUser(): any {
  try {
    const raw = localStorage.getItem("user");
    if (!raw || raw === "undefined") return {};
    const parsed = JSON.parse(raw);
    return parsed?.data?.user || parsed?.user || parsed || {};
  } catch {
    return {};
  }
}

export function getLoggedInEmpId(): string {
  const user = getLoggedInUser();
  return String(
    user?.empId || user?.details?.empId || user?.usrid || user?.emp_id || ""
  ).trim();
}

export function padEmpId(empId = getLoggedInEmpId()): string {
  const raw = String(empId || "").trim();
  if (!raw) return "";
  return raw.padStart(8, "0");
}

export function getEmployeeDisplayName(user = getLoggedInUser()): string {
  const details = user?.details || {};
  return (
    [details?.firstname, details?.lsastname || details?.secondname || details?.lastname]
      .filter(Boolean)
      .join(" ")
      .trim() || "-"
  );
}

export function unwrap(res: any): any {
  return res?.data ?? res;
}

export function extractResults(data: any): any[] {
  const root = unwrap(data);
  const inner = root?.data ?? root;
  if (Array.isArray(inner?.items)) return inner.items;
  if (Array.isArray(inner?.data?.items)) return inner.data.items;
  if (Array.isArray(inner?.d?.results)) return inner.d.results;
  if (Array.isArray(inner?.results)) return inner.results;
  if (Array.isArray(inner?.data)) return inner.data;
  if (Array.isArray(inner)) return inner;
  if (Array.isArray(root?.items)) return root.items;
  if (Array.isArray(root?.d?.results)) return root.d.results;
  if (Array.isArray(root?.data)) return root.data;
  if (Array.isArray(root)) return root;
  return [];
}

export function extractOne(res: any): any {
  const root = unwrap(res);
  const inner = root?.data ?? root;
  if (Array.isArray(inner?.data)) return inner.data[0] ?? inner.data;
  if (inner != null && typeof inner === "object" && !Array.isArray(inner)) {
    if (inner.data && typeof inner.data === "object" && !Array.isArray(inner.data) && (inner.data.id || inner.data.ReferenceNumber)) {
      return inner.data;
    }
    return inner;
  }
  if (root != null && typeof root === "object" && (root.id || root.ReferenceNumber)) return root;
  return inner ?? root ?? null;
}

export function formatSapDate(value: any): string {
  if (value === undefined || value === null || value === "") return "-";
  const str = String(value).trim();
  const msMatch = str.match(/\/Date\((\d+)\)\//);
  if (msMatch) {
    const date = new Date(parseInt(msMatch[1], 10));
    if (isNaN(date.getTime())) return str;
    return toDmy(date);
  }
  const iso = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`;
  const dmy = str.match(/^(\d{2})[-/.](\d{2})[-/.](\d{4})/);
  if (dmy) return `${dmy[1]}/${dmy[2]}/${dmy[3]}`;
  const date = new Date(str);
  if (!isNaN(date.getTime())) return toDmy(date);
  return str;
}

export function formatSapDateTime(value: any): string {
  if (!value) return "-";
  const str = String(value).trim();
  const msMatch = str.match(/\/Date\((\d+)\)\//);
  const date = msMatch ? new Date(parseInt(msMatch[1], 10)) : new Date(str);
  if (isNaN(date.getTime())) return str;
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;
  return `${dd}/${mm}/${date.getFullYear()} ${String(hours).padStart(2, "0")}:${minutes} ${ampm}`;
}

function toDmy(date: Date): string {
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  return `${dd}/${mm}/${date.getFullYear()}`;
}

export function toYmd(dateStr: string): string {
  if (!dateStr) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
  return dateStr;
}

export function getStatusMeta(status: string) {
  const key = String(status || "").trim().toUpperCase();
  return ID_CARD_STATUS_MAP[key] || { labelKey: "", label: status || "Unknown", cls: "bg-secondary text-white" };
}

export function isDraftStatus(status: string): boolean {
  return String(status || "").trim().toUpperCase() === "DRAFT";
}

export function wasRecordCreated(res: any): boolean {
  const resData = unwrap(res);
  return Boolean(
    resData?.id ||
    resData?.ReferenceNumber ||
    resData?.data?.id ||
    resData?.data?.ReferenceNumber ||
    resData?.data?.data?.id ||
    resData?.data?.data?.ReferenceNumber
  );
}

export function isSubmitSuccess(res: any): boolean {
  if (res?.success === true || res?.status === 200 || res?.status === 201) return true;
  if (wasRecordCreated(res)) return true;
  return false;
}

export async function findAllIdCards(payload: {
  page?: number;
  limit?: number;
  startDate?: string;
  endDate?: string;
  status?: string;
  empId?: string;
}): Promise<{ list: any[]; page: number; totalPages: number }> {
  const body: any = {
    page: payload.page ?? 1,
    limit: payload.limit ?? 10,
    startDate: payload.startDate || "",
    endDate: payload.endDate || "",
  };
  const status = String(payload.status || "").trim();
  if (status && status.toUpperCase() !== "ALL") {
    body.status = status.toUpperCase();
  }
  if (payload.empId) body.empId = payload.empId;

  const res: any = await Api.post(API_URLS.ID_CARD.FIND_ALL, body);
  if (res?.success === false) {
    throw new Error(res?.message || "Failed to load ID card requests");
  }
  const root = unwrap(res);
  const inner = root?.data ?? root;
  const list = extractResults(res);
  const page = Number(inner?.page ?? root?.page ?? body.page) || body.page;
  const totalPages = Number(inner?.totalPages ?? root?.totalPages ?? 1) || 1;
  return { list, page, totalPages };
}

export async function findOneIdCard(id: string | number): Promise<any> {
  const res: any = await Api.post(API_URLS.ID_CARD.FIND_ONE, { id });
  if (res?.success === false) {
    throw new Error(res?.message || "Failed to load request details");
  }
  return extractOne(res);
}

export async function fetchIdCardMaster(): Promise<any[]> {
  const res: any = await Api.post(API_URLS.ID_CARD.MASTER, {});
  if (res?.success === false) {
    throw new Error(res?.message || "Failed to load ID card types");
  }
  return extractResults(res);
}

export async function syncIdCard(empId: string): Promise<any> {
  const res: any = await Api.post(API_URLS.ID_CARD.SYNC, { empId });
  if (res?.success === false) {
    throw new Error(res?.message || "Sync failed");
  }
  return res;
}

export async function createOrUpdateIdCard(payload: any): Promise<any> {
  try {
    const res: any = await Api.post(API_URLS.ID_CARD.CREATE, payload);
    if (isSubmitSuccess(res)) return res;
    if (res?.success === false || (typeof res?.status === "number" && res.status >= 400)) {
      if (wasRecordCreated(res)) return res;
      throw new Error(res?.message || res?.error || res?.data?.message || "Failed to submit application");
    }
    return res;
  } catch (error: any) {
    const errResData = error?.response?.data || error?.data || error;
    if (wasRecordCreated(errResData)) return errResData;
    throw error;
  }
}

export async function approveRejectIdCard(payload: {
  ReferenceNumber: string;
  Action: "A" | "R";
  HRemarks?: string;
}): Promise<any> {
  const res: any = await Api.post(API_URLS.ID_CARD.APPROVE_REJECT_ACTION, payload);
  if (res?.success === false) {
    throw new Error(res?.message || "Action failed");
  }
  return res;
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = String(reader.result || "");
      const base64 = result.includes(",") ? result.split(",")[1] : result;
      resolve(base64);
    };
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });
}

export async function uploadIdCardFile(file: File): Promise<string> {
  const filestring = await fileToBase64(file);
  const res: any = await Api.post(API_URLS.AWS_BUCKET.UPLOAD_FILE, {
    filestring,
    mime_type: file.type || "image/jpeg",
    module: "id-cards",
  });
  const key =
    res?.key ||
    res?.data?.key ||
    res?.url ||
    res?.data?.url ||
    res?.data?.data?.key ||
    "";
  if (!key) throw new Error("File upload failed");
  return String(key);
}

export async function getIdCardDocumentUrl(s3Key: string): Promise<string | null> {
  if (!s3Key) return null;
  if (/^https?:\/\//i.test(s3Key)) return s3Key;
  try {
    const res: any = await Api.post(API_URLS.AWS_BUCKET.GET_PRESIGNED_URL, { key: s3Key });
    const url =
      (typeof res === "string" && res) ||
      (typeof res?.data === "string" && res.data) ||
      (typeof res?.url === "string" && res.url) ||
      (typeof res?.data?.url === "string" && res.data.url) ||
      (typeof res?.data?.data === "string" && res.data.data) ||
      "";
    return url || null;
  } catch {
    return null;
  }
}

export function extractFileKey(file: any): string {
  return String(
    file?.FileName || file?.fileName || file?.key || file?.awsKey || file?.name || ""
  ).trim();
}

export function extractFileDisplayName(file: any): string {
  const key = extractFileKey(file);
  if (!key) return "Attachment";
  return key.split("/").pop() || key;
}

export function collectAttachments(detail: any): Array<{ file: any; section: string }> {
  const toDocuments: any[] = Array.isArray(detail?.ToDocument?.results) ? detail.ToDocument.results : [];
  const toImages: any[] = Array.isArray(detail?.ToImage?.results) ? detail.ToImage.results : [];
  const toSigns: any[] = Array.isArray(detail?.ToSign?.results) ? detail.ToSign.results : [];
  const extraDocs: any[] = Array.isArray(detail?.Attachments?.results)
    ? detail.Attachments.results
    : Array.isArray(detail?.documents)
      ? detail.documents
      : Array.isArray(detail?.attachments)
        ? detail.attachments
        : [];
  return [
    ...toImages.map((d) => ({ file: d, section: "Photo" })),
    ...toSigns.map((d) => ({ file: d, section: "Signature" })),
    ...toDocuments.map((d) => ({ file: d, section: "Document" })),
    ...extraDocs.map((d) => ({ file: d, section: "Attachment" })),
  ];
}

export function collectRemarks(detail: any): any[] {
  if (Array.isArray(detail?.ToRemarks?.results)) return detail.ToRemarks.results;
  if (Array.isArray(detail?.ToRemarks)) return detail.ToRemarks;
  if (Array.isArray(detail?.RemarksHistory)) return detail.RemarksHistory;
  if (Array.isArray(detail?.remarksHistory)) return detail.remarksHistory;
  return [];
}

export function purposeText(typeId: string | undefined, master: any[]): string {
  if (!typeId) return "-";
  const match = master.find(
    (item: any) =>
      String(item?.type_id || "")
        .trim()
        .toUpperCase() === String(typeId).trim().toUpperCase()
  );
  const raw = match?.type_text ?? typeId;
  return String(raw).replace(/^\d+\)\s*/, "").trim();
}
